import React, { useState, useEffect } from "react";
import adminNotificationService from "../../../../services/adminNotificationService";
import { useAuth } from "../../../../context/authContext";
import { Bell, Check, Trash2, Filter, Clock } from "lucide-react";

const AdminNotifications = () => {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all"); // all, unread, read
  const [selectedNotifications, setSelectedNotifications] = useState([]);
  const { user } = useAuth();

  useEffect(() => {
    fetchNotifications();
  }, []);

  const fetchNotifications = async () => {
    try {
      setLoading(true);
      const res = await adminNotificationService.getAdminNotifications();
      setNotifications(res);
    } catch (error) {
      console.error("Fehler beim Laden der Benachrichtigungen:", error);
    } finally {
      setLoading(false);
    }
  };

  const markAsRead = async (notificationId) => {
    try {
      await adminNotificationService.markNotificationsAsRead(notificationId);
      fetchNotifications();
    } catch (error) {
      console.error("Fehler beim Markieren als gelesen:", error);
    }
  };

  const markSelectedAsRead = async () => {
    try {
      await Promise.all(
        selectedNotifications.map((id) =>
          adminNotificationService.markNotificationsAsRead(id)
        )
      );
      setSelectedNotifications([]);
      fetchNotifications();
    } catch (error) {
      console.error("Fehler beim Markieren als gelesen:", error);
    }
  };

  const deleteNotification = async (notificationId) => {
    try {
      await adminNotificationService.deleteNotification(notificationId);
      fetchNotifications();
    } catch (error) {
      console.error("Fehler beim Löschen der Benachrichtigung:", error);
    }
  };

  const getFormattedDate = (dateString) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat("de-DE", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    }).format(date);
  };

  const filteredNotifications = notifications.filter((notification) => {
    const isRead = notification.readBy.includes(user._id);
    if (filter === "unread") return !isRead;
    if (filter === "read") return isRead;
    return true;
  });

  const getNotificationTypeIcon = (type) => {
    switch (type) {
      case "feedback":
        return "💬";
      case "system":
        return "🔧";
      case "alert":
        return "⚠️";
      default:
        return "📌";
    }
  };

  if (loading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-blue-500 border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-4">
      <div className="mb-6 flex items-center justify-between">
        <h1 className="text-2xl font-bold">Benachrichtigungen</h1>
        <div className="flex gap-4">
          <div className="flex items-center gap-2">
            <Filter className="h-5 w-5 text-gray-500" />
            <select
              className="rounded-lg border border-gray-300 bg-white px-3 py-2"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
            >
              <option value="all">Alle</option>
              <option value="unread">Ungelesen</option>
              <option value="read">Gelesen</option>
            </select>
          </div>
          {selectedNotifications.length > 0 && (
            <button
              onClick={markSelectedAsRead}
              className="flex items-center gap-2 rounded-lg bg-blue-500 px-4 py-2 text-white hover:bg-blue-600"
            >
              <Check className="h-5 w-5" />
              Ausgewählte als gelesen markieren
            </button>
          )}
        </div>
      </div>

      <div className="rounded-lg border border-gray-200 bg-white">
        {filteredNotifications.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12">
            <Bell className="h-12 w-12 text-gray-400" />
            <p className="mt-4 text-gray-500">
              Keine Benachrichtigungen vorhanden
            </p>
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {filteredNotifications.map((notification) => {
              const isRead = notification.readBy.includes(user._id);
              return (
                <div
                  key={notification._id}
                  className={`flex items-start gap-4 p-4 hover:bg-gray-50 ${
                    !isRead ? "bg-blue-50" : ""
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={selectedNotifications.includes(notification._id)}
                    onChange={() =>
                      setSelectedNotifications((prev) =>
                        prev.includes(notification._id)
                          ? prev.filter((id) => id !== notification._id)
                          : [...prev, notification._id]
                      )
                    }
                    className="mt-1 h-4 w-4 rounded border-gray-300"
                  />
                  <div className="flex-1">
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-2">
                        <span className="text-xl">
                          {getNotificationTypeIcon(notification.category)}
                        </span>
                        <p className="font-medium">{notification.message}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-sm text-gray-500">
                          <Clock className="mr-1 inline-block h-4 w-4" />
                          {getFormattedDate(notification.createdAt)}
                        </span>
                        {!isRead && (
                          <button
                            onClick={() => markAsRead(notification._id)}
                            className="rounded p-1 hover:bg-gray-100"
                            title="Als gelesen markieren"
                          >
                            <Check className="h-5 w-5 text-blue-500" />
                          </button>
                        )}
                        <button
                          onClick={() => deleteNotification(notification._id)}
                          className="rounded p-1 hover:bg-gray-100"
                          title="Löschen"
                        >
                          <Trash2 className="h-5 w-5 text-red-500" />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminNotifications;
